<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import { actionStore } from "@common/actionStore";

// quicklink: http://localhost/?quicklink=1288ad7f2f73ed36fc721fc1706f4b4e

const editorSettings = {};

const workflowEditorConfig = {
    cId: "workflowEditor",
    cType: "workflowEditor",
    cTitle: "Workflow Editor",
    cTemplate: `<div class="workflow-editor">
        <div class="action-area">
            <control name="WORKFLOW_LIST" :value="selectedWorkflow" v-if="!isWfListReloading"></control>
        </div>
        <div v-if="!controls.WORKFLOW_LIST.value">
            <p class="workflow-message">
                1. Select a workflow from the list above, or create a <control name="NEW_WORKFLOW" class="inline-link-button"></control> to begin.<br/>
                2. After selecting a workflow, add an activity to start building your workflow.
            </p>
            <div style="text-align: center;">
                <control name="NEW_WORKFLOW" style="display: inline-block;"></control>
            </div>
        </div>
        <div class="selected-workflow-container" v-if="controls.WORKFLOW_LIST.value">
            <div class="action-area">
                <control name="SAVE_WORKFLOW" style="margin-top: auto;"></control>
            </div>
            <div>
            <tab-list cId="tabList_workflowEditor" :prop-tabs-list="editorTabs">
                <template #tabList_item_0>
                    <div class="top-actions-panel">
                        <control name="ACTIVITY_LIST"></control>
                        <control name="ADD_ACTIVITY" style="align-self: flex-end; margin: 4px 15px 4px 4px;"></control>
                        <control name="NEW_ACTIVITY" style="align-self: flex-end; margin: 4px 15px 4px 4px;"></control>
                        <control name="TOGGLE_VISIBILITY" style="justify-content: flex-end; text-align: right;" :value="editorVisible" @update:value="editorVisible = $event;"></control>
                    </div>
                    <workflow-viewer
                        :c-id="'workflowViewer_default'"
                        @update:targetedItem="selectActivity"
                        @update:itemOrder="updateOrder"
                        :prop-editor-workflow-settings="computedWorkflowSettings"
                        :prop-editor-visible="editorVisible"
                    ></workflow-viewer>
                </template>
                <template #tabList_item_1>
                    <div>
                        <control name="WORKFLOW_TITLE" :value="workflowSettings.WORKFLOW_TITLE" @update:value="workflowSettings.WORKFLOW_TITLE = $event;"></control>
                        <control name="WORKFLOW_DESC" :value="workflowSettings.WORKFLOW_DESC" @update:value="workflowSettings.WORKFLOW_DESC = $event;"></control>
                        <control name="WORKFLOW_DEFAULT_STATUS" :value="workflowSettings.WORKFLOW_DEFAULT_STATUS" @update:value="workflowSettings.WORKFLOW_DEFAULT_STATUS = $event;"></control>
                        <control name="WORKFLOW_DISABLE_AUTO_STATUS_UPDATES" class="disable-auto-status-updates" :value="workflowSettings.WORKFLOW_DISABLE_AUTO_STATUS_UPDATES" @update:value="workflowSettings.WORKFLOW_DISABLE_AUTO_STATUS_UPDATES = $event;"></control>
                        <control v-if="controls.WORKFLOW_LIST.value > 0" name="WORKFLOW_DELETE"></control>
                    </div>
                </template>
                <template #tabList_item_2>
                    <div>
                    <div class="add-workflow-controls">
                        <control name="ADD_USAGE_ORG" class="add-org-dropdown"></control>
                        <control name="ADD_USAGE_BUTTON"></control>
                    </div>
                    <control name="USAGE_ORGS"></control>
                    </div>
                </template>
            </tab-list>
            </div>
        </div>
        <teleport v-if="targetedActivityIndex || targetedActivityIndex === 0" :to="targetedActivityElemId">
            <div id="activity-actions-panel">
                <control name="EDIT_ACTIVITY" @click.stop=""></control>
                <control name="REMOVE_ACTIVITY" @click.stop=""></control>
            </div>
        </teleport>
        <div class="action-area" v-if="controls.WORKFLOW_LIST.value">
            <control name="SAVE_WORKFLOW"></control>
        </div>
    </div>`,
    controls: [
        {
            name: 'WORKFLOW_LIST',
            type: 'dropdown',
            label: 'Workflows',
            itemsSource: 'configStore.workflowOptions()',
            fields: { text: 'text', value: 'value' },
            onchange: 'selectWorkflow'
        }, {
            name: 'WORKFLOW_TITLE',
            type: 'string',
            label: 'Title',
            placeholder: 'Give your workflow a recongnizable title.',
            cssClass: '',
            required: true
        }, {
            name: 'WORKFLOW_DESC',
            type: 'text',
            label: 'Description',
            placeholder: 'Describe your workflow for other coordinators to understand.',
            required: true
        }, {
            name: 'WORKFLOW_DEFAULT_STATUS',
            type: 'dropdown',
            label: 'Default Status',
            placeholder: 'Default Status',
            required: true,
            items: [
                { text: 'Complete', value: 3 },
                { text: 'In Progress', value: 2 },
                { text: 'Not Started', value: 1 },
                { text: 'Waiting', value: 4 },
                { text: 'Transferred', value: 5 }
            ],
            fields: { text: 'text', value: 'value' }
        }, {
            name: 'WORKFLOW_DISABLE_AUTO_STATUS_UPDATES',
            type: 'checkbox',
            label: 'Disable Automatic Status Updates',
        }, {
            name: 'WORKFLOW_DELETE',
            type: 'button',
            label: 'Delete Workflow',
            cssClass: '',
            onclick: 'deleteWorkflow',
            width: '200px'
        }, {
            name: 'ACTIVITY_LIST',
            type: 'dropdown',
            label: 'Activities',
            onchange: 'newActivity',
            placeholder: 'Select Activity',
            allowFiltering: true,
            itemsSource: 'configStore.activityOptions()',
            fields: {
                text: 'AE_TITLE',
                value: 'ACTIVITY_ENTITY_ID'
            }
        }, {
            name: 'ADD_USAGE_ORG',
            type: 'multiselect',
            label: 'Add Organizations',
            allowFiltering: true,
            fields: {
                text: 'ORG_TITLE',
                value: 'rowID'
            },
            itemsFilter: 'this.item.WORKFLOW_ID !== this.controls.WORKFLOW_LIST.value',
            itemsSource: [{
                source: ['ec_getGrid'],
                params: {
                    gridPsp: 'org',
                    gridRecord: false,
                    gridFields: ['ORG_TITLE', 'ORG_KIND', 'WORKFLOW_ID']
                }
            }],
            helpText: 'Select new organizations to use this workflow.'
        }, {
            name: 'ADD_USAGE_BUTTON',
            type: 'button',
            label: 'Add',
            cssClass: 'e-info add-org-button',
            onclick: 'addWorkflowToOrg'
        }, {
            name: 'USAGE_ORGS',
            type: 'list',
            label: 'Organizations Using This Workflow',
            itemsFilter: 'this.item.WORKFLOW_ID.indexOf(this.controls.WORKFLOW_LIST.value) !== -1',
            itemsSource: [{
                source: ['ec_getGrid'],
                params: {
                    gridPsp: 'org',
                    gridRecord: false,
                    gridFields: ['ORG_TITLE', 'ORG_KIND', 'WORKFLOW_ID']
                }
            }],
            itemContentTemplate: `<div class="workflow-org-record">
                <div style="flex-grow: 1;">
                    <strong>{{data.ORG_TITLE}}</strong><br/>{{data.ORG_KIND}}<br/>
                </div>
                <control name="REMOVE_USAGE_ORG" :actionParams="data.rowID"></control>
            </div>`
        }, {
            name: 'REMOVE_USAGE_ORG',
            type: 'button',
            label: 'Remove',
            cssClass: 'e-link',
            onclick: 'removeWorkflowFromOrg'
        }, {
            name: 'SAVE_WORKFLOW',
            type: 'button',
            label: 'Save Workflow',
            onclick: 'saveWorkflow',
            cssClass: 'save-workflow-button',
            display: (typeof VUE_PROJECT_NAME !== 'undefined' && VUE_PROJECT_NAME !== 'coordinator')
                || (typeof PROJECT_NAME !== 'undefined' && PROJECT_NAME !== 'coordinator'),
            logic: [
                {
                    record: 'self',
                    prop: 'disabled',
                    valueType: 'criteria',
                    criteria: '!this.controls.WORKFLOW_LIST.value'
                }
            ]
        }, {
            name: 'NEW_WORKFLOW',
            type: 'button',
            label: 'New Workflow',
            onclick: 'newWorkflow',
            cssClass: '',
            logic: [
                {
                    record: 'self',
                    prop: 'display',
                    valueType: 'criteria',
                    criteria: '!this.controls.WORKFLOW_LIST.value'
                }
            ]
        }, {
            name: 'ADD_ACTIVITY',
            type: 'button',
            label: 'Add',
            onclick: 'addActivity',
            cssClass: '',
            logic: [
                {
                    record: 'self',
                    prop: 'disabled',
                    valueType: 'criteria',
                    criteria: '!this.controls.ACTIVITY_LIST.value'
                }
            ],
            message: 'Select an activity and click add.'
        }, {
            name: 'NEW_ACTIVITY',
            type: 'button',
            label: 'New',
            onclick: 'selectNewActivity',
            cssClass: '',
            logic: [
                {
                    record: 'self',
                    prop: 'disabled',
                    valueType: 'criteria',
                    criteria: 'this.controls.ACTIVITY_LIST.value'
                }
            ],
            message: 'Select an activity and click add.'
        }, {
            name: 'TOGGLE_VISIBILITY',
            type: 'boolean',
            label: 'Make hidden items visible',
            inlineLabel: true,
            cssClass: '',
            default: true
        }, {
            name: 'EDIT_ACTIVITY',
            type: 'button',
            label: 'Edit',
            onclick: 'editActivity',
            cssClass: ''
        }, {
            name: 'REMOVE_ACTIVITY',
            type: 'button',
            label: 'Delete',
            onclick: 'removeActivity',
            cssClass: ''
        }
    ]
};

const workflowEditorCmp = {
    name: "workflowEditor",
    editorSettings: editorSettings,
    data() {
        return {
            workflowSettings: {
                activities: {},
                ACTIVITY_ENTITY_ID: []
            },
            editorTabs: [
                {
                    title: 'Activities List',
                    useVueSlot: true,
                }, {
                    title: 'General',
                    useVueSlot: true,
                }, {
                    title: 'Usage',
                    useVueSlot: true,
                }
            ],
            showTitleDesc: false,
            showActivityList: false,
            targetedActivity: false,
            targetedActivityId: false,
            targetedActivityIndex: false,
            targetedActivityElemId: false,
            editorVisible: true,
            activitySubscriptions: {},
            user: false,
            prevWorkflowSettings: {},
            selectedWorkflow: '',
            isWfListReloading: false
        };
    },
    computed: {
        computedWorkflowSettings() {
            let settings = library.cloneObj(this.workflowSettings);
            // Convert activity IDs to activities
            if (Array.isArray(settings.ACTIVITY_ENTITY_ID)) {
                // eslint-disable-next-line max-len
                let aeFields = ['ACTIVITY_ENTITY_ID', 'AE_TITLE', 'AE_DESCRIPTION', 'AE_INCLUDED_BY_DEFAULT', 'AE_INCLUDED_LOGIC_FILTER_ID', 'AE_DEFAULT_AFF_STATUS', 'AE_DEFAULT_AFF_STATUS_ID', 'AE_INCLUDED_AFF_STATUS', 'AE_INCLUDED_AFF_STATUS_ID', 'AE_ENABLED_BY_DEFAULT', 'AE_ENABLED_LOGIC_FILTER_ID', 'AE_ENABLED_AFF_STATUS', 'AE_ENABLED_AFF_STATUS_ID', 'AE_COMPLETED_BY_DEFAULT', 'AE_COMPLETED_LOGIC_FILTER_ID', 'AE_COMPLETED_AFF_STATUS', 'AE_COMPLETED_AFF_STATUS_ID', 'AE_ONCE_AND_DONE', 'AE_ONCE_AND_DONE_INCLUDE_FILTER_ID', 'ACTIVITY_TYPE_ID', 'ACTIVITY_ENTITY_TITLE', 'ACTIVITY_ENTITY_MESSAGE', 'ACTIVITY_ENTITY_ACTION', 'AE_ENABLED_SET', 'AE_ENABLED_SET_AFF_STATUS_ID', 'AE_ENABLED_SET_AFF_REASON_ID', 'AE_ENABLED_SET_AFF_START_DATE', 'AE_COMPLETED_SET_AFF_END_DATE'];
                settings.ACTIVITY_ENTITY_ID.forEach((activityId, index) => {
                    if (!this.activitySubscriptions[activityId]) {
                        this.activitySubscriptions[activityId] = this.recordStore.subscribe({
                            source: 'sds_getProfileField',
                            params: { recordId: activityId, params: { strictTypeResults: true }, fields: aeFields },
                            updateFrequency: 0
                        }, this.cId);
                    }
                    let subscriptionData = this.recordStore.getSubscription(this.activitySubscriptions[activityId].key);
                    let activitySub = library.cloneObj(subscriptionData);
                    settings.activities[index] = activitySub[activityId];
                });
            }
            return settings;
        },
    },
    methods: {
        manualSave() {
            // called when Apply/Finish button clicked in Profile VMS-8955
            return this.saveWorkflow();
        },
        selectWorkflow(event) {
            this.resetWorkflowConfig();
            let workflowId = event.value;
            this.selectedWorkflow = workflowId;
            if (!workflowId) return;
            if (workflowId === -1) this.newWorkflow();
            this.prevWorkflowSettings = library.cloneObj(event.itemData.details);
            library.mergeObj(this.workflowSettings, event.itemData.details);
        },
        resetWorkflowConfig() {
            this.workflowSettings.WORKFLOW_ID = false;
            this.workflowSettings.WORKFLOW_TITLE = '';
            this.workflowSettings.WORKFLOW_DESC = '';
            this.workflowSettings.WORKFLOW_DEFAULT_STATUS = null;
            this.workflowSettings.WORKFLOW_DISABLE_AUTO_STATUS_UPDATES = false;
            this.workflowSettings.activities = {};
            this.workflowSettings.ACTIVITY_ENTITY_ID = [];
            this.prevWorkflowSettings = {};
        },
        newWorkflow() {
            if (this.controls.WORKFLOW_LIST.value !== -1) this.controls.WORKFLOW_LIST.value = -1;
            this.workflowSettings.WORKFLOW_ID = -1;
            this.workflowSettings.WORKFLOW_TITLE = 'A New Workflow';
            this.workflowSettings.WORKFLOW_DESC = 'Description';
            this.workflowSettings.WORKFLOW_DEFAULT_STATUS = 2;
            this.workflowSettings.WORKFLOW_DISABLE_AUTO_STATUS_UPDATES = false;
        },
        async saveWorkflow() {
            let postData = library.cloneObj(this.workflowSettings);
            if (!this.workflowSettings.WORKFLOW_ID) return; // Exit out on unexpected UI behaviors
            if (
                !this.controls.WORKFLOW_TITLE.valid
                || !this.controls.WORKFLOW_DESC.valid
                || !this.controls.WORKFLOW_DEFAULT_STATUS.valid
            ) {
                await library.showDialog('Please fill in all required fields.');
                return;
            }
            delete postData.activities; // activities prop is for interface only
            library.showLoadingIndicator();
            let response;
            if (this.workflowSettings.WORKFLOW_ID === -1) {
                delete postData.WORKFLOW_ID;
                response = await this.sDs.post('sds_createWorkflow', postData);
            } else {
                response = await this.sDs.post('sds_updateWorkflow', postData.WORKFLOW_ID, postData);
            }
            if (response && response.status === 'success') {
                library.showToast('Workflow saved successfully!');
                if (this.workflowSettings.WORKFLOW_ID === -1) {
                    this.selectedWorkflow = response.data;
                    this.reloadWorkflowList();
                }
                if (
                    this.workflowSettings.WORKFLOW_ID !== -1
                    && !library.objIsEmpty(this.prevWorkflowSettings)
                    && Array.isArray(this.prevWorkflowSettings.ACTIVITY_ENTITY_ID)
                    && Array.isArray(this.workflowSettings.ACTIVITY_ENTITY_ID)
                ) {
                    let newActivityIsAdded = this.workflowSettings.ACTIVITY_ENTITY_ID.some((tmpEl) => this.prevWorkflowSettings.ACTIVITY_ENTITY_ID.indexOf(tmpEl) === -1);
                    if (newActivityIsAdded) {
                        await library.showDialog('Do you want to create affiliations for the new activity for already affiliated volunteers?', () => {
                            this.sDs.post('sds_affiliateVolWithOrgActivities', false, false, false, postData.WORKFLOW_ID);
                        }, false, { name: 'confirmActivityAffiliation' });
                    }
                }
            } else library.showToast('Failure: We could not save your workflow.  Please try again.');
            library.hideLoadingIndicator();
        },
        selectNewActivity() {
            this.controls.ACTIVITY_LIST.value = -1;
            // this.newActivity();
        },
        async newActivity(event) {
            if (event.value === -1) {
                this.targetedActivityId = -1; // Creates a new activity
                let response = await this.editActivity();
                this.controls.ACTIVITY_LIST.value = null;
                if (typeof response !== 'object') {
                    library.showToast('Action cancelled');
                    return;
                }
                let activityId = response.data;
                this.workflowSettings.ACTIVITY_ENTITY_ID.push(activityId);
                // wait for details to load, and get activity title for message below
                library.showToast('New activity created, ID:' + activityId);
            }
        },
        selectActivity(item) {
            this.targetedActivity = false;
            this.targetedActivityId = false;
            this.targetedActivityIndex = false;
            this.targetedActivityElemId = '[id="' + item.elemId + '"]';
            setTimeout(() => {
                this.targetedActivity = item;
                this.targetedActivityId = item.uiRecordId;
                this.targetedActivityIndex = item.itemIndex;
            }, 100);
        },
        updateOrder(event) {
            let tmpActivityIds = [];
            let currActivites = this.workflowSettings.ACTIVITY_ENTITY_ID;
            for (let index in event.itemOrder) {
                if (index === 'length') continue;
                let actIndex = event.itemOrder[index];
                tmpActivityIds[index] = currActivites[actIndex];
            }
            this.workflowSettings.ACTIVITY_ENTITY_ID = tmpActivityIds;
        },
        addActivity() {
            if (!this.controls.ACTIVITY_LIST.value) return;
            this.workflowSettings.ACTIVITY_ENTITY_ID.push(this.controls.ACTIVITY_LIST.value);
            this.controls.ACTIVITY_LIST.value = null;
        },
        async editActivity() {
            if (!this.targetedActivityId) return false;
            let relatedRecords = {
                'wf': this.workflowSettings
            };
            return actionStore.openProfile({ recordPsp: 'ae', recordId: this.targetedActivityId, relatedRecords: relatedRecords });
        },
        removeActivity() {
            let activityIndex = this.targetedActivityIndex;
            this.workflowSettings.ACTIVITY_ENTITY_ID.splice(activityIndex, 1);
        },
        async removeWorkflowFromOrg(_event, control) {
            let orgId = control.actionParams;
            let wfId = this.controls.WORKFLOW_LIST.value;
            let WORKFLOW_ID = library.cloneObj(this.recordStore.records.org[orgId].WORKFLOW_ID);
            WORKFLOW_ID.splice(WORKFLOW_ID.indexOf(wfId), 1);
            let response = await this.sDs.post('sds_updateProfileField', 'org', orgId, { WORKFLOW_ID });
            if (response.status === 'success') {
                library.showDialog('Success! The workflow has been removed from this organizaton.', false, false, { showAsAlert: true });
                this.recordStore.records.org[orgId].WORKFLOW_ID = WORKFLOW_ID;
                this.recordStore.setModified({ org: [orgId] });
            } else if (response.status === 'failure') {
                library.showDialog('Failure. Removing the workflow was not successful.', false, false, { showAsAlert: true });
            }
        },
        async addWorkflowToOrg() {
            let orgIds = this.controls.ADD_USAGE_ORG.value;
            if (!orgIds) {
                library.showDialog('Select an organization to add.', false, false, { showAsAlert: true });
                return;
            }
            if (!Array.isArray(orgIds)) orgIds = [orgIds];
            let wfId = this.controls.WORKFLOW_LIST.value;
            let promises = [];
            let updatedOrgIds = [];
            orgIds.forEach((orgId) => {
                // Orgs should already be in the recordStore from ADD_USAGE_ORG control
                let WORKFLOW_ID = library.cloneObj(this.recordStore.records.org[orgId].WORKFLOW_ID);
                if (WORKFLOW_ID.indexOf(wfId) === -1) {
                    WORKFLOW_ID.push(wfId);
                    promises.push(this.sDs.post('sds_updateProfileField', 'org', orgId, { WORKFLOW_ID }));
                    updatedOrgIds.push(orgId); // For response handling
                }
            });
            let responses = await library.promiseAllSettled(promises);
            let failedOrgs = [];
            let successOrgs = [];
            responses.forEach((res, index) => {
                let updatedOrg = this.recordStore.records.org[updatedOrgIds[index]];
                if (res.value.status === 'success') {
                    updatedOrg.WORKFLOW_ID.push(wfId);
                    successOrgs.push(updatedOrg.ORG_TITLE);
                }
                else failedOrgs.push(updatedOrg.ORG_TITLE);
            });
            if (updatedOrgIds.length) this.recordStore.setModified({ org: updatedOrgIds });
            if (successOrgs.length) library.showDialog('<strong>Success!</strong><br/>The selected organization(s) are now using this workflow: ' + successOrgs.join(', ') + '<br/><br/>You should now see them in the list below.', false, false, { showAsAlert: true });
            if (failedOrgs.length) library.showDialog('<strong>Failure</strong><br/>We were not able to set the workflow for the following organizations: ' + failedOrgs.join(', '), false, false, { showAsAlert: true });
        },
        async reloadWorkflowList() {
            await configStore.workflowOptions(true);
            this.isWfListReloading = true;
            this.$nextTick(() => {
                this.isWfListReloading = false;
            });
        },
        deleteWorkflow() {
            if (this.selectedWorkflow > 0) {
                library.showDialog('Are you sure you want to delete this workflow?', async () => {
                    let response = await this.sDs.post('sds_deleteWorkflow', this.selectedWorkflow);
                    if (response && response.status === 'success') {
                        this.selectedWorkflow = null;
                        this.reloadWorkflowList();
                        library.showToast('Workflow deleted successfully!');
                    } else {
                        let message = Array.isArray(response.error) ? response.error.join('<br/>') : 'Workflow not deleted';
                        library.showToast(message, { type: 'warning' });
                    }
                }, false);
            }
        },
        onLoad() {
            setTimeout(() => {
                let currentValue = this?.controls?.WORKFLOW_LIST?.value;
                if (currentValue) {
                    let itemData = this.controls.WORKFLOW_LIST.items.find((item) => item.value === currentValue);
                    this.selectWorkflow({
                        value: currentValue,
                        itemData
                    });
                }
            }, 1000);
        }
    },
    components: {
        profile: defineAsyncComponent(() => import('@common/profile.vue')),
        tabList: defineAsyncComponent(() => import('@common/tabList/tabList.vue'))
    }
};
componentStore.initComponent(workflowEditorCmp, workflowEditorConfig);

export default workflowEditorCmp;
</script>

<style scoped>
    .top-actions-panel {
        display: flex;
        justify-content: flex-end;
    }
    #activity-actions-panel {
        position: absolute;
        right: 0;
        top: 0;
        width: 50px;
        overflow: visible;
        background: rgb(229, 88, 113);
    }
    .action-area {
        margin: 20px 0;
        display: flex;
        justify-content: flex-end;
    }
    .action-area > div + div {
        margin-left: 10px;
    }
    .save-workflow-button {
        flex-shrink: 0
    }
    .workflow-message {
        font-size: 16px;
        background: #fff3c5;
        border: 1px solid #f5ce45;
        color: #4f3a05;
        padding: 10px;
        text-align: center;
        border-radius: 5px;
    }
    .inline-link-button {
        display: inline-block;
    }
    .inline-link-button :deep(button) {
        background: rgba(255, 255, 255, 0.7);
        padding: 4px;
        border-radius: 5px;
        box-shadow: none;
        text-transform: lowercase;
        text-decoration: underline;
    }
    .edit-icon-button {
        background: #E7F8FF7F;
        padding: 4px;
        border-radius: 5px;
        border: 0 none;
    }
    .edit-icon-button:hover {
        background: rgba(231, 248, 255, 1);
    }
    .e-icons.e-edit:before {
        content: '\e891' !important;
    }
    :deep(.workflow-org-record) {
        padding: 10px;
        border: 1px solid #f0f0f0;
        border-bottom: 1px solid white;
        background: rgba(0, 0, 0, 0.05);
        display: flex;
    }
    :deep(.workflow-org-record button span) {
        text-transform: lowercase
    }
    :deep(.add-workflow-controls) {
        display: flex;
        align-items: flex-end;
        margin-bottom: 15px;
    }
    :deep(.add-org-dropdown) {
        margin-right: 15px;
    }
    :deep(.add-org-button) {
        padding-left: 15px;
        padding-right: 15px;
    }
    :deep(.disable-auto-status-updates) {
        text-align: left;
        margin-top: 10px;
    }
</style>
